import CKEditorWrapper from '@/components/CKEditorWrapper';
import CKFileUpload from '@/components/CKFileUpload/CKFileUpload';
import ContainerForm from '@/components/ContainerForm';
import { LayoutSectionComponent } from '@/components/LayoutSection';
import { appData } from '@/lib/data';
import { Translations } from '@/lib/translation';
import {
  convertQuestionDataTypeToQuizQuestion,
  convertQuizQuestionToQuestionDataType,
  getCkEditorConfig,
  getCkFileType,
  getCkFinderConfig,
  isCompulsoryQuestionCountEnough,
  isEnabledQuestionCountEnough,
  openPlayerUIWindow,
} from '@/lib/util';
import {
  useCreateChoiceMutation,
  useDeleteChoiceMutation,
  useDeleteQuestionMutation,
  useGetElearningQuery,
  useGetQuestionQuery,
  useGetQuestionsQuery,
  useGetQuizQuery,
  useUpdateChoiceMutation,
  useUpdateQuestionMutation,
} from '@/modules/services';
import { Choice, QuestionResponseType } from '@/modules/types/choice';
import { CkFinderResourceTypeEnum } from '@/modules/types/ckFinder';
import { QuizQuestion } from '@/modules/types/question';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Col,
  DeleteIcon,
  Dialog,
  FormContainer,
  FormItem,
  Modal,
  MoreHorizIcon,
  NativeSelect,
  Popover,
  RemoveRedEyeOutlinedIcon,
  Row,
  Text,
} from 'elmo-elements';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import QuizQuestionsRoutes, { QuestionParamType } from '../Questions/routes';
import { Choices, responseTypeOptions } from './Choices';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import PreviewFeedbackRoutes from '@/pages/PreviewFeedback/route';
import { showQuestionImage } from '@/components/Player/FrameContent/util';

const questionLengthLimit = 1000;
const feedbackLengthLimit = 1000;
const schema = yup.object().shape({
  id: yup.string().max(50),
  question: yup
    .string()
    .max(questionLengthLimit, `Max ${questionLengthLimit} characters`)
    .required('Must have a question'),
  image: yup.mixed().optional(),
  sound: yup.mixed().optional(),
  position: yup.number().optional(),
  compulsory: yup.number().min(0).max(1),
  disabled: yup.number().min(0).max(1),
  responseType: yup.number().oneOf(
    responseTypeOptions.map((item) => item.value),
    'Must be one of the selection values',
  ),
  choices: yup
    .array()
    .of(
      yup.object().shape({
        choiceId: yup.string().max(50),
        position: yup
          .number()
          .min(0)
          .max(25, 'Exceeded maximum number of choices.'),
        type: yup.number().min(1).max(2),
        isCorrect: yup.boolean(),
        content: yup
          .string()
          .max(500, 'Max characters of 500 ')
          .required('Content is required.'),
      }),
    )
    .when('responseType', {
      // Only use this validation if reponse type is Match - 4
      is: (val: number) => val === QuestionResponseType.match,
      then: (schem) =>
        schem
          .of(
            yup.object().shape({
              choiceId: yup.string().max(50),
              position: yup
                .number()
                .min(0)
                .max(25, 'Exceeded maximum number of choices.'),
              type: yup.number().min(1).max(2),
              isCorrect: yup.boolean(),
              content: yup
                .string()
                .max(64, 'Max characters of 64.')
                .required('Content is required.'),
            }),
          )
          .min(2, 'There should be at least 2 choices.'),
    })
    .when('responseType', {
      // Only use this validation if reponse type is Order - 5
      is: (val: number) => val === QuestionResponseType.order,
      then: (schem) =>
        schem
          .of(
            yup.object().shape({
              choiceId: yup.string().max(50),
              position: yup
                .number()
                .min(0)
                .max(25, 'Exceeded maximum number of choices.'),
              type: yup.number().min(1).max(2),
              isCorrect: yup.boolean(),
              content: yup
                .string()
                .max(64, 'Max characters of 64 ')
                .required('Content is required.'),
            }),
          )
          .min(2, 'There should be at least 2 choices.'),
    })
    .when('responseType', {
      // Only use this validation if reponse type is Traffic light - 6
      is: (val: number) => val === QuestionResponseType.trafficLight,
      then: (schem) =>
        schem
          .of(
            yup.object().shape({
              choiceId: yup.string().max(50),
              position: yup
                .number()
                .min(0)
                .max(3, 'Exceeded maximum number of choices.'),
              type: yup.number().min(1).max(2),
              isCorrect: yup.boolean(),
              content: yup
                .string()
                .max(64, 'Max characters of 64 ')
                .required('Content is required.'),
            }),
          )
          .min(3, 'There should only be 3 choices.')
          .max(3, 'There should only be 3 choices.'),
    }),
  correctfeedback: yup
    .string()
    .max(feedbackLengthLimit, `Max ${feedbackLengthLimit} characters`),
  incorrectfeedback: yup
    .string()
    .max(feedbackLengthLimit, `Max ${feedbackLengthLimit} characters`),
});

const defaultValues = {
  id: '',
  question: 'Question',
  compulsory: 0,
  disabled: 0,
  responseType: 1,
  sound: '',
  position: 0,
  image: '',
  answer: 0,
  multiAnswer: [],
  choices: [],
  // TODO: confirm w/ MAULIK -  implement default feedback values
  correctfeedback: '',
  incorrectfeedback: '',
};

const ckWordCountPlugin = {
  extraPlugins: 'wordcount,notification',
  wordcount: {
    // Whether or not you Show Remaining Count (if Maximum Word/Char/Paragraphs Count is set)
    showRemaining: true,
    // Whether or not you want to show the Paragraphs Count
    showParagraphs: false,
    // Whether or not you want to show the Word Count
    showWordCount: false,
    // Whether or not you want to show the Char Count
    showCharCount: true,
    // Whether or not you want to Count Bytes as Characters (needed for Multibyte languages such as Korean and Chinese)
    countBytesAsChars: false,
    // Whether or not you want to count Spaces as Chars
    countSpacesAsChars: true,
    // Whether or not to include Html chars in the Char Count
    countHTML: true,
    // Whether or not to include Line Breaks in the Char Count
    countLineBreaks: false,
    // Whether or not to prevent entering new Content when limit is reached.
    hardLimit: false,
    // Whether or not to to Warn only When limit is reached. Otherwise content above the limit will be deleted on paste or entering
    warnOnLimitOnly: false,
    // Maximum allowed Word Count, -1 is default for unlimited
    maxWordCount: -1,
    // Maximum allowed Char Count, -1 is default for unlimited
    maxCharCount: questionLengthLimit,
    // Maximum allowed Paragraphs Count, -1 is default for unlimited
    maxParagraphs: -1,
    // How long to show the 'paste' warning, 0 is default for not auto-closing the notification
    pasteWarningDuration: 0,
  },
};

export const QuestionForm = (): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isIncompleteModalOpen, setIsIncompleteModalOpen] =
    useState<boolean>(false);
  const [notEnoughQuestionModalOpen, setNotEnoughQuestionModalOpen] =
    useState<boolean>(false);
  const [isTypeChangeModalOpen, setIsTypeChangeModalOpen] =
    useState<boolean>(false);
  const [validationModalMessage, setValidationModalMessage] = useState<string>(
    Translations.quiz.error.noCorrectAnswerMessage,
  );
  const [responseTypeState, setResponseTypeState] = useState<number>(0);
  const ckeditorQuestion = useRef<any>(null);
  const ckeditorCorrectFeedback = useRef<any>(null);
  const ckeditorIncorrectFeedback = useRef<any>(null);

  const { elearningId, questionId } = useParams<QuestionParamType>();

  const elearning = useGetElearningQuery(elearningId);
  const { data: quizData } = useGetQuizQuery(elearningId);
  const { data: questionsData } = useGetQuestionsQuery(
    {
      quizId: `${quizData?.id}`,
    },
    {
      skip: !quizData?.id || !quizData,
    },
  );
  const { data: questionData, isSuccess: questionDataIsSuccess } =
    useGetQuestionQuery(
      {
        questionId,
      },
      {
        skip: !quizData?.id || !questionId,
      },
    );

  const questionsWithoutTheCurrent = useMemo(
    () => questionsData?.filter((question) => question.id !== questionId) ?? [],
    [questionId, questionsData],
  );

  const enabledQuestionCount = useMemo(
    () =>
      questionsWithoutTheCurrent.filter(
        ({ questionMetadata }) => questionMetadata.disabled !== 1,
      )?.length ?? 0,
    [questionsWithoutTheCurrent],
  );

  const compulsoryQuestionCount = useMemo(
    () =>
      questionsWithoutTheCurrent.filter(
        ({ questionMetadata }) =>
          questionMetadata.compulsory === 1 && questionMetadata.disabled === 0,
      )?.length ?? 0,
    [questionsWithoutTheCurrent],
  );

  const defaultQuestionsValues = !questionData
    ? defaultValues
    : convertQuestionDataTypeToQuizQuestion(
        questionData,
        questionData.choices,
        QuestionResponseType.match,
        QuestionResponseType.trueOrFalse,
      );

  const hasNoCorrectChoices = (
    choices: Choice[],
    responseType: QuestionResponseType,
    typesToFilter: number[],
  ): boolean => {
    const isCorrectChoices =
      choices.filter((choice) => choice.isCorrect === true) || [];

    return isCorrectChoices.length <= 0 && typesToFilter.includes(responseType);
  };

  const useFormMethods = useForm<QuizQuestion>({
    resolver: yupResolver(schema),
    defaultValues: defaultQuestionsValues,
  });

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    control,
  } = useFormMethods;
  const question = watch('question');
  const correctfeedback = watch('correctfeedback');
  const incorrectfeedback = watch('incorrectfeedback');

  const isQuestionAskedValidOffset =
    !!Number(watch('compulsory')) && !watch('disabled') ? 1 : 0;

  const { remove } = useFieldArray({
    name: 'choices',
    control,
  });

  const hasNoAnswer = (
    choices: Choice[],
    responseType: QuestionResponseType,
  ) => {
    const typesToFilter = [
      QuestionResponseType.singleAnswer,
      QuestionResponseType.multiCorrect,
      QuestionResponseType.trafficLight,
    ];

    return hasNoCorrectChoices(choices, +responseType, typesToFilter);
  };

  useEffect(() => {
    if (questionData)
      reset(
        convertQuestionDataTypeToQuizQuestion(
          questionData,
          questionData.choices,
          QuestionResponseType.match,
          QuestionResponseType.trueOrFalse,
        ),
      );

    if (ckeditorQuestion.current)
      ckeditorQuestion.current.setData(getValues('question'));
    if (ckeditorCorrectFeedback.current)
      ckeditorCorrectFeedback.current.setData(getValues('correctfeedback'));
    if (ckeditorIncorrectFeedback.current)
      ckeditorIncorrectFeedback.current.setData(getValues('incorrectfeedback'));
  }, [questionData, reset, getValues]);

  const [updateQuestion, updateQuestionStatus] = useUpdateQuestionMutation();
  const [deleteQuestion, deleteQuestionStatus] = useDeleteQuestionMutation();
  const [createChoice, createChoiceStatus] = useCreateChoiceMutation();
  const [updateChoice, updateChoiceStatus] = useUpdateChoiceMutation();
  const [deleteChoice] = useDeleteChoiceMutation();

  const handleChange = (key: any, value: any) => {
    setValue(key, value);
  };

  const handleResponseChange = (type: QuestionResponseType) => {
    handleChange('responseType', type);
    remove();
    setIsTypeChangeModalOpen(false);
  };

  const deleteChoices = (choicesIdNotToDelete: string[]) => {
    if (questionData?.choices) {
      questionData.choices.forEach((choice) => {
        if (!choicesIdNotToDelete.includes(choice.id)) {
          deleteChoice(choice.id);
        }
      });
    }
  };

  const handleDeleteQuestion = (id: string) => {
    deleteChoices([]);
    deleteQuestion(id);
  };

  const submitChoices = (choicesSelection: Choice[], id: string) => {
    const choicesIdNotToDelete: string[] = [];

    choicesSelection.forEach(async (choice) => {
      if (choice.type !== undefined && choice.position !== undefined) {
        const lcmsReferenceIds = questionData?.choices?.find(
          (item) => item.id === choice.choiceId,
        )?.lcmsReferenceIds;

        const choiceData = {
          id: choice.choiceId || '',
          content: {
            id: choice.choiceId,
            content: choice.content,
            lcmsReferenceIds,
            choiceMetadata: {
              type: choice.type,
              position: choice.position,
              isCorrect: choice.isCorrect,
              useUUID: choice.useUUID || true,
            },
          },
        };

        if (choice.choiceId) {
          updateChoice(choiceData);
          choicesIdNotToDelete.push(choice.choiceId);
        } else {
          choiceData.id = id;
          delete choiceData.content.id;
          createChoice(choiceData);
        }
      }
    });

    deleteChoices(choicesIdNotToDelete);
  };

  const onSubmit = (data: QuizQuestion) => {
    const disabledOffset = data.disabled ? 0 : 1;
    if (
      !isEnabledQuestionCountEnough({
        offset: disabledOffset,
        questionAsked: quizData?.quizMetadata.questionNo,
        enabledQuestionCount,
      })
    ) {
      setNotEnoughQuestionModalOpen(true);
      return;
    }

    const compulsoryOffset = data.compulsory && !data.disabled ? 1 : 0;
    if (
      !isCompulsoryQuestionCountEnough({
        offset: compulsoryOffset,
        questionAsked: quizData?.quizMetadata.questionNo,
        compulsoryQuestionCount,
      })
    ) {
      setValidationModalMessage(Translations.quiz.error.compulsoryMessage);
      setIsIncompleteModalOpen(true);
      return;
    }

    if (hasNoAnswer(data.choices || [], +data.responseType)) {
      setValidationModalMessage(Translations.quiz.error.noCorrectAnswerMessage);
      setIsIncompleteModalOpen(true);
      return;
    }

    const submitData = {
      id: questionId,
      content: convertQuizQuestionToQuestionDataType(data),
    };

    updateQuestion(submitData);

    const choicesToSubmit = [...(data.choices || [])];

    submitChoices(choicesToSubmit, submitData.content.id || '');
  };

  const handleCkUpload = (
    fieldName: keyof QuizQuestion,
    currentFileName: string,
  ) => {
    setValue(fieldName, currentFileName);
  };

  const handleFeedbackPreview = (feedbackType: 'correct' | 'incorrect') => {
    if (!elearning.data) return;

    openPlayerUIWindow(
      elearning.data,
      generatePath(PreviewFeedbackRoutes.path, {
        elearningId,
        questionId,
        type: feedbackType,
      }),
      'Feedback preview',
      appData.playerTheme.default.windowMetadata,
    );
  };

  useEffect(() => {
    if (deleteQuestionStatus.isSuccess || deleteQuestionStatus.isError) {
      const message = deleteQuestionStatus.isSuccess
        ? 'Delete question successful.'
        : 'Delete question error! Something went wrong.';
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message,
        },
      });
    }
  }, [deleteQuestionStatus]);

  useEffect(() => {
    if (updateQuestionStatus.isSuccess || updateQuestionStatus.isError) {
      const message = updateQuestionStatus.isSuccess
        ? 'Question saved successfully'
        : 'Update question error! Something went wrong.';
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message,
        },
      });
    }
  }, [updateQuestionStatus]);

  useEffect(() => {
    // Redirect out of question on successful delete
    if (questionDataIsSuccess && !questionData) {
      history.push(
        generatePath(QuizQuestionsRoutes.path, {
          elearningId,
          questionId: '*',
        }),
      );
    }
  }, [questionData]);

  if (!questionData) return <div className="loading" />;

  return (
    <>
      <Modal
        type="small"
        isOpened={isIncompleteModalOpen}
        closeModal={() => setIsIncompleteModalOpen(false)}
        title="Incomplete question"
        closeLabel="Close"
        primaryButton={
          <Button
            type="primary"
            isBlock={true}
            onClick={() => setIsIncompleteModalOpen(false)}
          >
            Ok, got it.
          </Button>
        }
      >
        {validationModalMessage}
      </Modal>
      <Modal
        type="small"
        isOpened={notEnoughQuestionModalOpen}
        closeModal={() => setNotEnoughQuestionModalOpen(false)}
        title="Not enough question"
        closeLabel="Close"
        primaryButton={
          <Button
            type="primary"
            isBlock={true}
            onClick={() => setNotEnoughQuestionModalOpen(false)}
          >
            Ok, got it.
          </Button>
        }
      >
        Not enough question on quizzes.
      </Modal>
      <Modal
        type="small"
        isOpened={isTypeChangeModalOpen}
        closeModal={() => setIsTypeChangeModalOpen(false)}
        title="Change response type"
        closeLabel="Close"
        primaryButton={
          <Button
            type="danger"
            isBlock={true}
            onClick={() => {
              handleResponseChange(responseTypeState);
            }}
          >
            Continue
          </Button>
        }
      >
        Changing the response type will discard all choice items associated with
        this question. Are you sure you want to select a new response type?
      </Modal>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ContainerForm maxColumns={2}>
          <LayoutSectionComponent
            isFirst={true}
            heading={`Question ${
              questionData.questionMetadata.position !== undefined
                ? `${+questionData.questionMetadata.position + 1}`
                : ''
            }`}
            content={
              <FormContainer maxColumns={2}>
                <FormItem
                  label=""
                  message={errors.question?.message}
                  status={errors.question ? 'error' : 'default'}
                  isBlock={true}
                >
                  <CKEditorWrapper
                    {...register('question', {})}
                    name="question"
                    initData={questionData?.content || getValues('question')}
                    onInstanceReady={(instance) => {
                      ckeditorQuestion.current = instance.editor;
                    }}
                    value={question}
                    setValue={(name, value) => setValue(name, value)}
                    toolbar="extended"
                    config={{
                      ...getCkEditorConfig({
                        tenantId: `${elearning.data?.tenantId}`,
                        type: 'quizzes',
                        elearningId,
                      }),
                      ...ckWordCountPlugin,
                    }}
                  />
                </FormItem>

                {showQuestionImage(+getValues('responseType')) && (
                  <FormItem
                    label="Question Image"
                    message={errors.image?.message}
                    status={errors.image ? 'error' : 'default'}
                    isBlock={true}
                    className="elmo-formitem__single-file"
                  >
                    <Text size="sm">
                      Max. 270px by 270px images. Only JPG, GIF and PNG file
                      types supported.
                    </Text>
                    <CKFileUpload
                      connectorDetails={getCkFinderConfig({
                        type: getCkFileType([CkFinderResourceTypeEnum.IMAGES]),
                        tenantId: elearning?.currentData?.tenantId ?? '',
                        entityId: elearningId,
                        folderName: 'quizzes',
                      })}
                      defaultFileName={getValues('image') ?? ''}
                      fileType="images"
                      handlerFunction={{
                        onFileChoose: (fileName) =>
                          handleCkUpload('image', fileName),
                      }}
                      register={register}
                      registerName="image"
                    />
                  </FormItem>
                )}

                <FormItem
                  label="Sound"
                  message={errors.sound?.message}
                  status={errors.sound ? 'error' : 'default'}
                  className="elmo-formitem__single-file"
                >
                  <CKFileUpload
                    connectorDetails={getCkFinderConfig({
                      type: getCkFileType([CkFinderResourceTypeEnum.SOUNDS]),
                      tenantId: elearning?.currentData?.tenantId ?? '',
                      entityId: elearningId,
                      folderName: 'quizzes',
                    })}
                    defaultFileName={getValues('sound') ?? ''}
                    fileType="sounds"
                    handlerFunction={{
                      onFileChoose: (fileName) =>
                        handleCkUpload('sound', fileName),
                    }}
                    register={register}
                    registerName="sound"
                  />
                </FormItem>

                <FormItem
                  label="Mandatory"
                  message={
                    !isCompulsoryQuestionCountEnough({
                      offset: isQuestionAskedValidOffset,
                      compulsoryQuestionCount,
                      questionAsked: quizData?.quizMetadata.questionNo,
                    })
                      ? 'Mandatory questions exceed number of questions to ask.'
                      : errors.compulsory?.message
                  }
                  status={
                    errors.compulsory ||
                    !isCompulsoryQuestionCountEnough({
                      offset: isQuestionAskedValidOffset,
                      compulsoryQuestionCount,
                      questionAsked: quizData?.quizMetadata.questionNo,
                    })
                      ? 'error'
                      : 'default'
                  }
                  size="sm"
                >
                  <NativeSelect
                    {...register('compulsory', {})}
                    value={watch('compulsory')}
                    onChange={(value: number | string) =>
                      handleChange('compulsory', value)
                    }
                    id={`elearning-compulsory-${questionId}`}
                    ariaLabel="Mandatory"
                  >
                    <NativeSelect.Option label="No" value={0} />
                    <NativeSelect.Option label="Yes" value={1} />
                  </NativeSelect>
                </FormItem>

                <FormItem
                  label="Disabled"
                  message={
                    !isEnabledQuestionCountEnough({
                      offset: watch('disabled') ? 0 : 1,
                      questionAsked: quizData?.quizMetadata.questionNo,
                      enabledQuestionCount,
                    })
                      ? 'Not enough enabled questions to cater for the required questions asked'
                      : errors.disabled?.message
                  }
                  status={
                    errors.disabled ||
                    !isEnabledQuestionCountEnough({
                      offset: watch('disabled') ? 0 : 1,
                      questionAsked: quizData?.quizMetadata.questionNo,
                      enabledQuestionCount,
                    })
                      ? 'error'
                      : 'default'
                  }
                  size="sm"
                >
                  <NativeSelect
                    {...register('disabled', {})}
                    value={watch('disabled')}
                    onChange={(value: number | string) =>
                      handleChange('disabled', Number(value))
                    }
                    id={`elearning-disabled-${questionId}`}
                    ariaLabel="Disabled"
                  >
                    <NativeSelect.Option label="No" value={0} />
                    <NativeSelect.Option label="Yes" value={1} />
                  </NativeSelect>
                </FormItem>
              </FormContainer>
            }
          />

          {questionId && (
            <LayoutSectionComponent
              heading="Responses"
              content={
                <>
                  {hasNoAnswer(
                    watch('choices') || [],
                    watch('responseType'),
                  ) && (
                    <Alert
                      message="Select the correct answer."
                      type="danger"
                      isCloseable={false}
                    />
                  )}
                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Response Type"
                      message={errors.responseType?.message}
                      status={errors.responseType ? 'error' : 'default'}
                      size="sm"
                    >
                      <NativeSelect
                        {...register('responseType', {})}
                        value={watch('responseType')}
                        onChange={(e: any) => {
                          setResponseTypeState(e);
                          setIsTypeChangeModalOpen(true);
                        }}
                        id={`elearning-response-type-${questionId}`}
                        ariaLabel="Response type"
                      >
                        {responseTypeOptions.map(({ label, value }) => (
                          <NativeSelect.Option
                            key={value}
                            label={label}
                            value={value}
                          />
                        ))}
                      </NativeSelect>
                    </FormItem>

                    <FormItem
                      size="sm"
                      message={
                        errors.choices?.message ? errors.choices.message : ''
                      }
                      status={errors.choices?.message ? 'error' : 'default'}
                    >
                      <FormProvider {...useFormMethods}>
                        <Choices responseType={+getValues('responseType')} />
                      </FormProvider>
                    </FormItem>
                  </FormContainer>
                </>
              }
            />
          )}

          <LayoutSectionComponent
            heading="Feedback"
            isCollapsible={true}
            content={
              <FormContainer maxColumns={2}>
                <FormItem
                  label="Correct response feedback"
                  message={errors.correctfeedback?.message}
                  status={errors.correctfeedback ? 'error' : 'default'}
                  isBlock={true}
                >
                  <CKEditorWrapper
                    {...register('correctfeedback', {})}
                    name="correctfeedback"
                    initData={
                      questionData?.feedbackMetadata?.feedback1 ||
                      getValues('correctfeedback')
                    }
                    onInstanceReady={(instance) => {
                      ckeditorCorrectFeedback.current = instance.editor;
                    }}
                    value={correctfeedback}
                    setValue={(name, value) => setValue(name, value)}
                    toolbar="extended"
                    config={{
                      ...getCkEditorConfig({
                        tenantId: `${elearning.data?.tenantId}`,
                        type: 'quizzes',
                        elearningId,
                      }),
                      ...ckWordCountPlugin,
                    }}
                  />
                  <Row justifyContent="end">
                    <Col isAuto={true} grow={0}>
                      <Button onClick={() => handleFeedbackPreview('correct')}>
                        <RemoveRedEyeOutlinedIcon className="margin-sides" />
                        Preview
                      </Button>
                    </Col>
                  </Row>
                </FormItem>
                <FormItem
                  label="Incorrect response feedback"
                  message={errors.incorrectfeedback?.message}
                  status={errors.incorrectfeedback ? 'error' : 'default'}
                  isBlock={true}
                >
                  <CKEditorWrapper
                    {...register('incorrectfeedback', {})}
                    name="incorrectfeedback"
                    initData={
                      questionData?.feedbackMetadata?.feedback2 ||
                      getValues('incorrectfeedback')
                    }
                    onInstanceReady={(instance) => {
                      ckeditorIncorrectFeedback.current = instance.editor;
                    }}
                    value={incorrectfeedback}
                    setValue={(name, value) => setValue(name, value)}
                    toolbar="extended"
                    config={{
                      ...getCkEditorConfig({
                        tenantId: `${elearning.data?.tenantId}`,
                        type: 'quizzes',
                        elearningId,
                      }),
                      ...ckWordCountPlugin,
                    }}
                  />
                  <Row justifyContent="end">
                    <Col isAuto={true} grow={0}>
                      <Button
                        onClick={() => handleFeedbackPreview('incorrect')}
                      >
                        <RemoveRedEyeOutlinedIcon className="margin-sides" />
                        Preview
                      </Button>
                    </Col>
                  </Row>
                </FormItem>
              </FormContainer>
            }
          />
        </ContainerForm>

        <Row className="mt-4" justifyContent="start" isNoGutters={true}>
          <Col isAuto={true} grow={0}>
            <Button
              type="primary"
              htmlType="submit"
              ariaLabel="Submit Form"
              isLoading={
                updateQuestionStatus.isLoading ||
                updateChoiceStatus.isLoading ||
                createChoiceStatus.isLoading
              }
              isUppercase={false}
              className="px-5"
            >
              Save
            </Button>
          </Col>
          <Col isAuto={true}>
            <Popover mode="click" isNoPadding={true}>
              <Popover.Target>
                <Button
                  icon={<MoreHorizIcon />}
                  isText={true}
                  className="ml-2"
                />
              </Popover.Target>
              <Popover.Content className="more-question-action-content">
                <Button
                  icon={<RemoveRedEyeOutlinedIcon />}
                  isText={true}
                  type="default"
                  ariaLabel="Preview Question"
                  isUppercase={false}
                  onClick={() => {
                    if (elearning.data) {
                      openPlayerUIWindow(
                        elearning.data,
                        `/preview-quiz/${elearning.data.id}/${questionId}`,
                        'preview',
                        appData.playerTheme.default.windowMetadata,
                      );
                    }
                  }}
                >
                  Preview Question
                </Button>

                <Button
                  icon={<RemoveRedEyeOutlinedIcon />}
                  type="default"
                  isText={true}
                  ariaLabel="Preview Quiz"
                  isUppercase={false}
                  onClick={() => {
                    if (elearning.data) {
                      openPlayerUIWindow(
                        elearning.data,
                        `/preview-quiz/${elearning.data.id}`,
                        'preview',
                        appData.playerTheme.default.windowMetadata,
                      );
                    }
                  }}
                >
                  Preview Quiz
                </Button>

                <Button
                  icon={<DeleteIcon />}
                  type="default"
                  isText={true}
                  ariaLabel="Delete Question"
                  isUppercase={false}
                  isLoading={deleteQuestionStatus.isLoading}
                  onClick={() => {
                    const isQuestionRemainingValid =
                      isEnabledQuestionCountEnough({
                        offset: 0,
                        questionAsked: quizData?.quizMetadata.questionNo,
                        enabledQuestionCount,
                      });
                    if (isQuestionRemainingValid) {
                      Dialog.confirm({
                        ariaLabel: 'Delete confirm',
                        title: `Delete question`,
                        content: `Are you sure you want to delete question?`,
                        onOk() {
                          handleDeleteQuestion(questionId);
                        },
                        okLabel: 'Delete',
                      });
                    } else {
                      setNotEnoughQuestionModalOpen(true);
                    }
                  }}
                >
                  Delete Question
                </Button>
              </Popover.Content>
            </Popover>
          </Col>
        </Row>
      </form>
    </>
  );
};

export default QuestionForm;
